<script>
import { required } from "vuelidate/lib/validators";
import createNumberMask from "text-mask-addons/dist/createNumberMask";

export default {
  name: "ActualCashTransactionsModal",
  components: {
    DatePicker: () => import(/* webpackPrefetch: true */ "vue2-datepicker"),
    MaskedInput: () => import(/* webpackPrefetch: true */ "vue-text-mask"),
  },
  data() {
    return {
      modalTitle: "Actual Cash Transactions",
      fields: [
        {
          key: "date",
          label: "Date",
          tdClass: "align-middle",
        },
        {
          key: "description",
          label: "Description",
          tdClass: "align-middle",
        },
        {
          key: "debit",
          label: "Debit",
          tdClass: "align-middle",
        },
        {
          key: "credit",
          label: "Credit",
          tdClass: "align-middle align-right",
        },
        {
          key: "balance",
          label: "Balance",
          tdClass: "align-middle align-right",
        },
      ],
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      sortBy: "date",
      sortDesc: false,
      filter: null,
      filterOn: [],
      alert: {
        show: false,
        type: "",
        message: "",
      },
      type: null,
      transactions: {
        date: Date.now(),
        description: null,
        amount: "0",
        cycle: null,
      },
      submitted: false,
      currencyMask: createNumberMask({
        prefix: "",
        allowDecimal: true,
      }),
      transactionModalTitle: "",
      transactionTable: [],
      fundSource: null,
    };
  },
  validations: {
    transactions: {
      date: { required },
      description: { required },
      amount: { required },
    },
  },
  computed: {
    rows() {
      return this.transactionTable ? this.transactionTable.length : 0;
    },
  },
  methods: {
    async fetchTransactions(fundSource) {
      if (!fundSource) return;
      this.fundSource = fundSource;
      this.transactionTable = [];
      try {
        const { data: response } = await this.$store.dispatch(
          "actualcash/getActualCashTransactions",
          {
            fundSourceId: fundSource.id,
            cycle: this.$store.state.data.cycle,
          }
        );
        if (response.error) {
          return this.showToast(response.errorMessage, "danger");
        }
        this.transactionTable = response;
        this.totalRows = response.length;
      } catch (error) {
        this.showToast("Something went wrong. Cannot fetch transactions", "danger");
      }
    },
    closeModal() {
      this.$bvModal.hide("actual-cash-transactions-modal");
    },
    resetModal() {
      this.filter = null;
      this.currentPage = 1;
      this.reset();
    },
    formatCurrency(data) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "PHP",
      }).format(data);
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    formatDateComponent(data) {
      return new Date(data).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },
    showModal(type) {
      if (this.fundSource && this.fundSource.isExpired) {
        this.$swal({
          title: "Expired Fund Source",
          text: "This fund source has expired. You cannot add more transactions.",
          icon: "warning",
          showCancelButton: false,
          confirmButtonText: "OK",
        });
        return;
      }
      this.type = type;
      this.transactionModalTitle = `Add Actual Cash Transaction - ${this.type.toUpperCase()}`;
      this.$bvModal.show("cashModal");
    },
    addTransaction() {
      this.submitted = true;
      this.$v.$touch();
      if (this.$v.transactions.$invalid) {
        return;
      } else {
        if (
          this.type === "credit" &&
          Number(this.getExactAmt(this.transactions.amount)) >
          Number(this.fundSource.actualCashBalance)
        ) {
          this.$swal({
            title: "Insufficient Balance",
            text: "The amount you entered is greater than the Actual Cash Balance.",
            icon: "warning",
            showCancelButton: false,
            confirmButtonText: "OK",
          });
          return;
        }
        const nDate = new Date(
          new Date(this.transactions.date).getTime() -
          new Date(this.transactions.date).getTimezoneOffset() * 60 * 1000
        );
        const nData = {
          fundSourceId: this.fundSource.id,
          description: this.transactions.description,
          transactionDate: nDate.toISOString(),
          debit:
            this.type === "debit"
              ? Number(this.getExactAmt(this.transactions.amount))
              : 0,
          credit:
            this.type === "credit"
              ? Number(this.getExactAmt(this.transactions.amount))
              : 0,
          cycle: this.$store.state.data.cycle,
        };
        this.$store
          .dispatch("actualcash/createActualCashTransactions", nData)
          .then((response) => {
            if (response.data.error) {
              return this.showToast(response.data.errorMessage, "danger");
            }
            this.showToast("Transaction added successfully", "success");
            this.$emit("update-balance", response.data.balance);
            this.fundSource.actualCashBalance = response.data.balance;
            this.transactionTable.push(response.data);
            this.hideTransaction();
          })
          .catch((error) => {
            this.showToast("Something went wrong. Cannot add transaction", "danger");
          });
      }
    },
    getExactAmt(value) {
      return value ? value.toString().replace(/[^0-9.]/g, '') : '0';
    },
    showToast(message, type) {
      this.$bvToast.toast(message, {
        title: type === 'success' ? 'Success' : 'Error',
        variant: type,
        solid: true
      });
    },
    reset() {
      this.submitted = false;
      this.$v.transactions.$reset();
      this.transactions = {
        date: Date.now(),
        description: null,
        amount: "0",
        cycle: this.$store.state.data.cycle,
      };
    },
    hideTransaction() {
      this.$bvModal.hide("cashModal");
      this.reset();
    },
  },
};
</script>
<template>
  <b-modal
    id="actual-cash-transactions-modal"
    :title="modalTitle"
    size="xl"
    hide-footer
  >
    <div>
      <div class="d-flex justify-content-between">
        <div class="pt-3 d-flex justify-content-between">
          <div class="me-5">
            <small class="d-block font-weight-light">Fund Source: </small>
            <h4 class="text-info">{{ fundSource ? fundSource.sourceName : '' }}</h4>
          </div>
          <div>
            <small class="d-block font-weight-light">Actual Cash Balance: </small>
            <h4 class="text-warning">{{ fundSource ? formatCurrency(fundSource.actualCashBalance) : '' }}</h4>
          </div>
        </div>
        <div class="text-end my-2">
          <b-dropdown variant="success" size="sm">
            <template slot="button-content">
              Add Transaction
              <i class="mdi mdi-chevron-down"></i>
            </template>
            <b-dropdown-item @click="showModal('debit')">
              <i class="bx bx-money font-size-16 align-middle me-2 text-info"></i>
              Add Funds
            </b-dropdown-item>
            <b-dropdown-item v-if="fundSource && fundSource.actualCashBalance > 1" @click="showModal('credit')">
              <i class="bx bx-transfer font-size-16 align-middle me-2 text-warning"></i>
              Revert
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>
      
      <div class="row">
        <div class="col-sm-12 text-right">
          <div id="tickets-table_filter" class="dataTables_filter text-md-end">
            <label class="d-inline-flex align-items-center">
              Search:
              <b-form-input
                v-model="filter"
                type="search"
                placeholder="Search..."
                class="form-control form-control-sm ms-2"
              ></b-form-input>
            </label>
          </div>
        </div>
      </div>
      
      <div class="table-responsive mb-0">
        <b-table
          class="table"
          :items="transactionTable"
          :fields="fields"
          responsive="sm"
          :per-page="perPage"
          :current-page="currentPage"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :filter="filter"
          :filter-included-fields="filterOn"
          @filtered="onFiltered"
          bordered
          outlined
          hover
          striped
          show-empty
        >
          <template #empty="scope">
            <div class="text-center">
              <h5 class="text-secondary">{{ scope.emptyText }}</h5>
            </div>
          </template>
          <template #table-busy>
            <div class="d-flex justify-content-center align-items-center">
              <div class="preloader-component me-2">
                <div class="status">
                  <div class="spinner-chase w-20px h-20px">
                    <div class="chase-dot"></div>
                    <div class="chase-dot"></div>
                    <div class="chase-dot"></div>
                    <div class="chase-dot"></div>
                    <div class="chase-dot"></div>
                    <div class="chase-dot"></div>
                  </div>
                </div>
              </div>
              <strong>Loading...</strong>
            </div>
          </template>
          <template #cell(debit)="row">
            <span :class="{ 'text-info': row.item.debit > 0 }">{{
              formatCurrency(row.item.debit)
            }}</span>
          </template>
          <template #cell(balance)="row">
            <span class="text-warning">{{ formatCurrency(row.item.balance) }}</span>
          </template>
          <template #cell(credit)="row">
            <span :class="{ 'text-pink': row.item.credit > 0 }">{{
              formatCurrency(row.item.credit)
            }}</span>
          </template>
          <template #cell(date)="row">
            <span>{{ formatDateComponent(row.item.transactionDate) }}</span>
          </template>
        </b-table>
      </div>
      
      <div class="row">
        <div class="col">
          <div class="dataTables_paginate paging_simple_numbers float-end">
            <ul class="pagination pagination-rounded mb-0">
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
              ></b-pagination>
            </ul>
          </div>
        </div>
      </div>
      
      <!-- Transaction Modal -->
      <b-modal
        id="cashModal"
        :title="transactionModalTitle"
        hide-footer
      >
        <div class="mb-3">
          <div>
            <label for="date">Transaction Date</label>
            <date-picker
              v-model="transactions.date"
              :first-day-of-week="1"
              lang="en"
              type="datetime"
              value-type="timestamp"
              format="YYYY-MM-DD hh:mm a"
              :class="{
                'is-invalid': submitted && $v.transactions.date.$error,
              }"
            ></date-picker>
            <div v-if="submitted && $v.transactions.date.$error" class="invalid-feedback">
              <span v-if="!$v.transactions.date.required">This value is required.</span>
            </div>
          </div>
        </div>
        <div class="mb-3">
          <label for="description">Description</label>
          <b-form-textarea
            v-model="transactions.description"
            id="description"
            placeholder="Enter description"
            class="form-control"
            autocomplete="off"
            rows="3"
            :class="{
              'is-invalid': submitted && $v.transactions.description.$error,
            }"
          >
          </b-form-textarea>
          <div v-if="submitted && $v.transactions.description.$error" class="invalid-feedback">
            <span v-if="!$v.transactions.description.required">This value is required.</span>
          </div>
        </div>
        <div class="mb-3">
          <label for="amount">Amount</label>
          <masked-input
            autocomplete="off"
            type="text"
            v-model="transactions.amount"
            placeholder="Enter Amount..."
            class="form-control"
            :class="{
              'is-invalid': submitted && $v.transactions.amount.$error,
            }"
            :mask="currencyMask"
          ></masked-input>
          <div v-if="submitted && $v.transactions.amount.$error" class="invalid-feedback">
            <span v-if="!$v.transactions.amount.required">This value is required.</span>
          </div>
        </div>
        <div class="modal-footer">
          <b-button variant="primary" @click="addTransaction"> Save </b-button>
          <b-button variant="link" @click="hideTransaction"> Close </b-button>
        </div>
      </b-modal>
    </div>
    
    <!-- Modal Footer -->
    <div class="modal-footer">
      <b-button variant="secondary" @click="closeModal">Close</b-button>
    </div>
  </b-modal>
</template>
<style scoped>
  .modal-footer {
    border-top: 1px solid #dee2e6;
    padding: 1rem;
    margin-top: 1rem;
  }
</style>